import { defineStore } from 'pinia';
import { supportApi } from '@/modules/support/api/supportApi.js';
import notifier from '@/core/utils/notifier.js';
import { useAuthStore } from '@/store/authStore.js';
import { getErrorMessage, BAN_ERRORS } from '@/core/utils/errorMapper.js';

export const useSupportStore = defineStore('supportChat', {
    state: () => ({
        tickets: [],
        selectedTicketId: null,
        messages: [],

        loadingTickets: false,
        loadingMessages: false,
        sendingMessage: false,
        sendingForm: false,
        creatingTicket: false,

        ticketsCursor: null,
        hasMoreTickets: false,

        messagesCursor: null,
        hasMoreMessages: false,

        searchQuery: '',
        statusFilter: 'all',

        ws: null,
        wsState: 'disconnected',
        wsReconnectTimeout: null,

        ticketsRequestVersion: 0,

        unreadCount: 0,
    }),

    getters: {
        selectedTicket(state) {
            return state.tickets.find(t => String(t.id) === String(state.selectedTicketId)) ?? null;
        },
        selectedMessages(state) {
            return state.messages.filter(m => String(m.ticket_id) === String(state.selectedTicketId));
        },
        openTicketsCount(state) {
            return state.tickets.filter(t => t.status === 'open').length;
        },
    },

    actions: {
        async fetchTickets(reset = false) {
            if (this.loadingTickets && !reset) return;

            const version = ++this.ticketsRequestVersion;
            this.loadingTickets = true;

            if (reset) {
                this.ticketsCursor = null;
                this.hasMoreTickets = false;
                this.tickets = [];
                this.selectedTicketId = null;
                this.messages = [];
                this.messagesCursor = null;
                this.hasMoreMessages = false;
            }

            try {
                const params = { limit: 30 };
                const q = this.searchQuery.trim();
                if (q) params.q = q;
                if (this.statusFilter !== 'all') params.status = this.statusFilter;
                if (!reset && this.ticketsCursor && this.ticketsCursor.last_time > 0 && this.ticketsCursor.last_id) {
                    params.last_time = this.ticketsCursor.last_time;
                    params.last_id = this.ticketsCursor.last_id;
                }

                const response = await supportApi.getTickets(params);
                if (version !== this.ticketsRequestVersion) return;

                const data = response?.data?.data || [];
                const nextCursor = response?.data?.next_cursor || null;
                const hasMore = response?.data?.has_more ?? false;

                if (reset) {
                    this.tickets = data;
                } else {
                    const existingIds = new Set(this.tickets.map(t => String(t.id)));
                    const newTickets = data.filter(t => !existingIds.has(String(t.id)));
                    this.tickets = [...this.tickets, ...newTickets];
                }

                this.ticketsCursor = nextCursor;
                this.hasMoreTickets = hasMore;

                if (this.tickets.length > 0 && !this.selectedTicketId) {
                    await this.selectTicket(this.tickets[0].id);
                } else if (this.tickets.length === 0) {
                    this.selectedTicketId = null;
                    this.messages = [];
                }
            } catch (err) {
                if (version !== this.ticketsRequestVersion) return;
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
                notifier.error('support-tickets-error', getErrorMessage(err?.response?.data?.error, 'Не удалось загрузить список обращений'));
            } finally {
                if (version === this.ticketsRequestVersion) {
                    this.loadingTickets = false;
                }
            }
        },

        async selectTicket(ticketId) {
            if (String(this.selectedTicketId) === String(ticketId) && this.messages.length > 0) return;

            this.selectedTicketId = ticketId;
            const ticket = this.tickets.find(t => String(t.id) === String(ticketId));
            if (ticket) ticket.unread = 0;

            this.messages = [];
            this.messagesCursor = null;
            this.hasMoreMessages = false;

            await this.fetchMessages(ticketId);
            await this.markAsRead(ticketId);
        },

        async fetchUnreadCount() {
            try {
                const response = await supportApi.getUnreadCount();
                this.unreadCount = response?.data?.unread_count ?? 0;
                return this.unreadCount;
            } catch {
                return this.unreadCount;
            }
        },

        async markAsRead(ticketId) {
            if (!ticketId) return;
            try {
                await supportApi.markAsRead(ticketId);
                const ticket = this.tickets.find(t => String(t.id) === String(ticketId));
                if (ticket) ticket.unread = 0;
                await this.fetchUnreadCount();
            } catch {
                // не критично, счётчик обновится при следующем входе
            }
        },

        async fetchMessages(ticketId, loadOlder = false) {
            if (this.loadingMessages) return;
            this.loadingMessages = true;

            try {
                const params = { limit: 50 };
                if (loadOlder && this.messagesCursor && this.messagesCursor.last_time > 0 && this.messagesCursor.last_id) {
                    params.last_time = this.messagesCursor.last_time;
                    params.last_id = this.messagesCursor.last_id;
                }

                const response = await supportApi.getMessages(ticketId, params);
                const rawData = response?.data?.data || [];
                const nextCursor = response?.data?.next_cursor || null;
                const hasMore = response?.data?.has_more ?? false;

                // Бэкенд возвращает сообщения от новых к старым.
                const ordered = [...rawData].reverse();

                if (loadOlder) {
                    const existingIds = new Set(this.messages.map(m => String(m.id)));
                    const olderUnique = ordered.filter(m => !existingIds.has(String(m.id)));
                    this.messages = [...olderUnique, ...this.messages];
                } else {
                    this.messages = ordered;
                }

                this.messagesCursor = nextCursor;
                this.hasMoreMessages = hasMore;
            } catch (err) {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
                notifier.error('support-messages-error', getErrorMessage(err?.response?.data?.error, 'Не удалось загрузить сообщения'));
            } finally {
                this.loadingMessages = false;
            }
        },

        async sendMessage(text) {
            if (!this.selectedTicketId) return;
            const trimmed = text.trim();
            if (!trimmed) {
                notifier.error('support-message-empty', 'Введите текст сообщения');
                return;
            }

            this.sendingMessage = true;
            try {
                const ticketId = this.selectedTicketId;
                const response = await supportApi.sendMessage(ticketId, trimmed);
                const newMessage = response?.data;

                if (newMessage) {
                    if (!this.messages.some(m => String(m.id) === String(newMessage.id))) {
                        this.messages.push(newMessage);
                    }
                    const ticket = this.tickets.find(t => String(t.id) === String(ticketId));
                    if (ticket) {
                        ticket.updated_at = newMessage.created_at || new Date().toISOString();
                    }
                }
            } catch (err) {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
                notifier.error('support-send-error', getErrorMessage(err?.response?.data?.error, 'Не удалось отправить сообщение'));
                throw err;
            } finally {
                this.sendingMessage = false;
            }
        },

        async sendForm(form = {}) {
            const email = String(form.email || '').trim();
            const type = String(form.type || '').trim();
            const message = String(form.message || '').trim();

            if (!email) {
                notifier.error('support-form-email-empty', getErrorMessage('SEND_FORM_EMAIL_EMPTY'));
                return false;
            }
            if (!message) {
                notifier.error('support-form-message-empty', getErrorMessage('SEND_FORM_MESSAGE_EMPTY'));
                return false;
            }
            if (!['support', 'offering', 'bug'].includes(type)) {
                notifier.error('support-form-invalid-type', getErrorMessage('SEND_FORM_INVALID_TYPE'));
                return false;
            }

            this.sendingForm = true;
            try {
                await supportApi.sendForm({email, type, message});
                notifier.success('support-form-sent', 'Сообщение успешно отправлено');
                return true;
            } catch (err) {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return false;
                notifier.error(
                    'support-form-error',
                    getErrorMessage(err?.response?.data?.error, 'Не удалось отправить сообщение. Попробуйте позже'),
                );
                return false;
            } finally {
                this.sendingForm = false;
            }
        },

        async createTicket(title, initialMessage = null) {
            const trimmed = title.trim();
            if (!trimmed) {
                notifier.error('support-title-empty', 'Введите заголовок обращения');
                return null;
            }

            this.creatingTicket = true;
            try {
                const response = await supportApi.createTicket(trimmed);
                const newTicket = response?.data;
                if (newTicket) {
                    this.tickets.unshift(newTicket);
                    this.selectedTicketId = newTicket.id;
                    this.messages = [];
                    this.messagesCursor = null;
                    this.hasMoreMessages = false;
                    await this.fetchMessages(newTicket.id);

                    if (initialMessage && initialMessage.trim()) {
                        await this.sendMessage(initialMessage.trim());
                    }
                }
                return newTicket;
            } catch (err) {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return null;
                notifier.error('support-create-error', getErrorMessage(err?.response?.data?.error, 'Не удалось создать обращение'));
                return null;
            } finally {
                this.creatingTicket = false;
            }
        },

        async toggleTicketStatus() {
            if (!this.selectedTicketId) return;
            const ticket = this.selectedTicket;
            if (!ticket) return;

            const newStatus = ticket.status === 'open' ? 'close' : 'open';
            try {
                await supportApi.updateTicketStatus(ticket.id, newStatus);
                ticket.status = newStatus;
                ticket.updated_at = new Date().toISOString();
            } catch (err) {
                notifier.error('support-status-error', getErrorMessage(err?.response?.data?.error, 'Не удалось изменить статус обращения'));
            }
        },

        // --- WebSocket ---

        connectWebSocket() {
            if (typeof window === 'undefined') return;
            const token = useAuthStore().access_token;
            if (!token) return;

            if (this.ws) this.disconnectWebSocket();

            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
            const getWsUrl = (jwtToken) => {
                if (apiBaseUrl) {
                    const wsBase = apiBaseUrl.replace(/^http/, 'ws');
                    return `${wsBase.replace(/\/$/, '')}/api/v1/ws?token=${jwtToken}`;
                }
                const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                return `${protocol}//${window.location.host}/api/v1/ws?token=${jwtToken}`;
            };

            const wsUrl = getWsUrl(token);
            this.wsState = 'connecting';

            try {
                const socket = new WebSocket(wsUrl);
                this.ws = socket;

                socket.onopen = () => {
                    this.wsState = 'connected';
                };

                socket.onerror = () => {
                    this.wsState = 'error';
                    this.scheduleReconnect();
                };

                socket.onclose = (event) => {
                    this.wsState = 'disconnected';
                    if (event.code === 1008) {
                        const authStore = useAuthStore();
                        if (BAN_ERRORS.has(event.reason)) {
                            this.disconnectWebSocket();
                            authStore.logout();
                            notifier.error('ws-access-revoked', 'Ваш аккаунт заблокирован администрацией.');
                        } else {
                            notifier.error('ws-access-revoked', getErrorMessage(event.reason, 'Доступ к чату ограничен. Обновите страницу или войдите заново'));
                        }
                        return;
                    }
                    this.scheduleReconnect();
                };

                socket.onmessage = (event) => {
                    try {
                        const payload = JSON.parse(event.data);
                        this.handleIncomingWsMessage(payload);
                    } catch {
                        // ignore parse errors
                    }
                };
            } catch {
                this.wsState = 'error';
                this.scheduleReconnect();
            }
        },

        scheduleReconnect(delay = 5000) {
            if (this.wsReconnectTimeout || this.wsState === 'connected') return;
            this.wsReconnectTimeout = setTimeout(() => {
                this.wsReconnectTimeout = null;
                if (useAuthStore().access_token) {
                    this.connectWebSocket();
                }
            }, delay);
        },

        disconnectWebSocket() {
            if (this.wsReconnectTimeout) {
                clearTimeout(this.wsReconnectTimeout);
                this.wsReconnectTimeout = null;
            }
            if (this.ws) {
                this.ws.onopen = null;
                this.ws.onerror = null;
                this.ws.onclose = null;
                this.ws.onmessage = null;
                this.ws.close(1000, 'Normal closure');
                this.ws = null;
                this.wsState = 'disconnected';
            }
        },

        async handleIncomingWsMessage(message) {
            if (!message || !message.type) return;

            const nestedType = message.type === 'new_notification' ? (message.data?.type || message.data?.event) : null;
            const eventType = nestedType || message.type;
            const eventData = message.data;

            if (message.type === 'new_message_support' || message.type === 'support_message') {
                const msgData = message.data?.message || message.data;
                if (!msgData) return;

                const ticketId = String(msgData.ticket_id || msgData.chat_id || '');
                if (!ticketId) return;

                const isCurrentTicket = this.selectedTicketId && String(this.selectedTicketId) === ticketId;

                const ticket = this.tickets.find(t => String(t.id) === ticketId);
                if (ticket) {
                    ticket.updated_at = msgData.created_at || new Date().toISOString();
                    if (!isCurrentTicket && msgData.is_admin) {
                        ticket.unread = (ticket.unread || 0) + 1;
                    }
                } else {
                    // Если тикета ещё нет в списке (новый тикет), обновляем тикеты без полного сброса состояния
                    await this.fetchTickets(false);
                    await this.fetchUnreadCount();
                }

                if (isCurrentTicket) {
                    const item = {
                        id: String(msgData.id || msgData.message_id || `ws-${Date.now()}`),
                        ticket_id: ticketId,
                        sender_id: String(msgData.sender_id || ''),
                        sender_username: msgData.sender_username || msgData.username || 'Пользователь',
                        text: msgData.text || '',
                        is_admin: !!msgData.is_admin,
                        is_read: !!msgData.is_read,
                        created_at: msgData.created_at || new Date().toISOString(),
                        updated_at: msgData.updated_at || new Date().toISOString(),
                    };

                    if (!this.messages.some(m => String(m.id) === item.id)) {
                        this.messages.push(item);
                    }

                    if (msgData.is_admin) {
                        await this.markAsRead(ticketId);
                    } else {
                        await this.fetchUnreadCount();
                    }
                }
            } else if (eventType === 'ticket_updated') {
                const ticketId = eventData?.ticket_id;
                const status = eventData?.status;
                if (!ticketId || !status) return;

                const ticket = this.tickets.find(t => String(t.id) === String(ticketId));
                if (!ticket) return;

                ticket.status = status;
                ticket.updated_at = eventData.occurred_at
                    ? new Date(eventData.occurred_at * 1000).toISOString()
                    : new Date().toISOString();

                // Если активен фильтр по статусу и тикет больше не подходит — убираем из списка
                if (this.statusFilter !== 'all' && this.statusFilter !== status) {
                    this.tickets = this.tickets.filter(t => String(t.id) !== String(ticketId));
                    if (String(this.selectedTicketId) === String(ticketId)) {
                        this.selectedTicketId = null;
                        this.messages = [];
                        this.messagesCursor = null;
                        this.hasMoreMessages = false;
                    }
                }
            } else if (eventType === 'ticket_created') {
                // Подтягиваем новые тикеты БЕЗ сброса текущего выбранного диалога (reset = false)
                await this.fetchTickets(false);
            } else if (message.type === 'access_revoked') {
                this.disconnectWebSocket();
                useAuthStore().logout();
                notifier.error('ws-access-revoked', 'Доступ отозван');
            }
        },

        resetFilters() {
            this.searchQuery = '';
            this.statusFilter = 'all';
        },
    },
    persist: false,
});
